/**
 * A lightweight DOM-based virtual joystick (no @babylonjs/gui dependency).
 *
 * Dynamic origin: the base appears wherever the finger first touches inside its
 * zone, so the thumb is always under the thumb. Returns a normalized vector in
 * the range [-1, 1] with a dead zone, y already flipped so "up on screen" is
 * positive (matching sim-space y = world Z, away from the camera).
 *
 * Pointer capture keeps a single finger bound to one stick, so the left and
 * right sticks can be driven simultaneously without stealing each other's moves.
 */
import { vec2, type Vec2 } from "@allstars/shared";

export interface VirtualJoystick {
  value(): Vec2;
  readonly active: boolean;
  dispose(): void;
}

export interface JoystickOptions {
  /** Element that listens for pointerdown and hosts the base/thumb visuals. */
  zone: HTMLElement;
  /** Fraction of travel ignored near center to stop drift. Default 0.15. */
  deadZone?: number;
  /** Max thumb travel in pixels = full deflection. Default 60. */
  maxRadiusPx?: number;
  /**
   * Called on release with the stick's final vector. Enables "drag to aim,
   * release to fire" — a near-zero vector means a tap (caller auto-aims).
   */
  onRelease?: (direction: Vec2) => void;
}

export const createVirtualJoystick = (opts: JoystickOptions): VirtualJoystick => {
  const { zone } = opts;
  const deadZone = opts.deadZone ?? 0.15;
  const maxRadius = opts.maxRadiusPx ?? 60;

  const base = document.createElement("div");
  base.className = "vstick-base";
  const thumb = document.createElement("div");
  thumb.className = "vstick-thumb";
  base.appendChild(thumb);
  base.style.display = "none";
  zone.appendChild(base);

  let pointerId: number | null = null;
  let originX = 0;
  let originY = 0;
  let value: Vec2 = vec2(0, 0);

  const onDown = (e: PointerEvent): void => {
    if (pointerId !== null) return; // already tracking a finger on this stick
    pointerId = e.pointerId;
    originX = e.clientX;
    originY = e.clientY;
    const rect = zone.getBoundingClientRect();
    base.style.left = `${e.clientX - rect.left}px`;
    base.style.top = `${e.clientY - rect.top}px`;
    thumb.style.transform = "translate(-50%, -50%)";
    base.style.display = "block";
    zone.setPointerCapture(e.pointerId);
    e.preventDefault();
  };

  const onMove = (e: PointerEvent): void => {
    if (e.pointerId !== pointerId) return;
    const dx = e.clientX - originX;
    const dy = e.clientY - originY;
    const len = Math.hypot(dx, dy);
    const nx = len > 0 ? dx / len : 0;
    const ny = len > 0 ? dy / len : 0;
    const clamped = Math.min(len, maxRadius);
    const tx = nx * clamped;
    const ty = ny * clamped;
    thumb.style.transform = `translate(calc(-50% + ${tx}px), calc(-50% + ${ty}px))`;

    let mag = clamped / maxRadius;
    mag = mag < deadZone ? 0 : (mag - deadZone) / (1 - deadZone);
    // Flip y so screen-up (negative client Y) becomes positive sim y.
    value = { x: nx * mag, y: -ny * mag };
    e.preventDefault();
  };

  const onUp = (e: PointerEvent): void => {
    if (e.pointerId !== pointerId) return;
    const released = value;
    pointerId = null;
    value = vec2(0, 0);
    base.style.display = "none";
    opts.onRelease?.(released);
    e.preventDefault();
  };

  zone.addEventListener("pointerdown", onDown);
  zone.addEventListener("pointermove", onMove);
  zone.addEventListener("pointerup", onUp);
  zone.addEventListener("pointercancel", onUp);

  return {
    value: () => value,
    get active(): boolean {
      return pointerId !== null;
    },
    dispose(): void {
      zone.removeEventListener("pointerdown", onDown);
      zone.removeEventListener("pointermove", onMove);
      zone.removeEventListener("pointerup", onUp);
      zone.removeEventListener("pointercancel", onUp);
      base.remove();
    },
  };
};
